package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.WebAnalyticsDto
import com.qrkontaq.data.dto.WebSessionDto
import com.qrkontaq.data.dto.WebSessionStepDto
import com.qrkontaq.data.repository.PlatformRepository
import com.qrkontaq.data.repository.PlatformResult
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlin.js.Date
import org.jetbrains.compose.web.css.px

/**
 * SESSIONS section: the landing -> sign-up funnel (from the shared summary) plus
 * a lazily loaded feed of recent sessions, each shown as a device/source header
 * and an ordered chain of step chips.
 */
@Composable
internal fun PlatformSessionsView(
    repository: PlatformRepository,
    data: WebAnalyticsDto,
    days: Int,
    site: String?,
) {
    var sessions by remember { mutableStateOf<List<WebSessionDto>?>(null) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(days, site) {
        loading = sessions == null
        sessions = (repository.webSessions(days, site, 50) as? PlatformResult.Ok)?.value ?: emptyList()
        loading = false
    }

    QpCard {
        QpTitle(PlatformT.funnelTitle)
        SpanText(
            PlatformT.funnelSubtitle,
            Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.5f)).margin(bottom = 14.px),
        )
        QpFunnel(
            listOf(
                PlatformT.funnelSessions to data.funnel.landingSessions,
                PlatformT.funnelPricing to data.funnel.pricingReached,
                PlatformT.funnelCta to data.funnel.ctaClicked,
                PlatformT.funnelAdmin to data.funnel.adminReached,
            ),
        )
        Box(
            Modifier.fillMaxWidth().margin(top = 12.px).padding(top = 12.px)
                .borderTop(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f)),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                SpanText(PlatformT.funnelSignups, Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.6f)))
                SpanText(
                    "${data.funnel.signups}",
                    Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).color(Color.rgb(0x7E, 0xE2, 0xBC))
                        .attrsModifier { classes("qp-display") },
                )
            }
        }
    }

    QpCard {
        QpTitle(PlatformT.sessionsTitle)
        val list = sessions
        when {
            loading && list == null -> SessionsSkeleton()
            list.isNullOrEmpty() ->
                SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
            else -> Column(Modifier.fillMaxWidth().gap(10.px)) { list.forEach { SessionCard(it) } }
        }
    }
}

@Composable
private fun SessionsSkeleton() {
    Column(Modifier.fillMaxWidth().gap(10.px)) {
        repeat(4) { Box(Modifier.fillMaxWidth().height(64.px).attrsModifier { classes("qp-skeleton") }) }
    }
}

@Composable
private fun SessionCard(session: WebSessionDto) {
    Box(
        Modifier.fillMaxWidth()
            .borderRadius(14.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Color.rgba(255, 255, 255, 0.07f))
            .padding(12.px),
    ) {
        Column(Modifier.fillMaxWidth().gap(9.px)) {
            Row(
                Modifier.fillMaxWidth().flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap).gap(8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (session.country != null) {
                    SpanText(countryFlag(session.country), Modifier.fontSize(16.px))
                }
                SpanText(
                    geoLabel(session),
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Color.rgb(0xF2, 0xF7, 0xF4)),
                )
                DeviceChip(session)
                SourceChip(session)
                Box(Modifier.styleModifier { property("flex", "1") })
                if (session.isNew) Badge(PlatformT.sessionNew, "#E8A33D")
                if (session.isActive) LiveBadge()
                SpanText(
                    "${formatDuration(session.durationSeconds)} · ${relativeTime(session.startedAt)}",
                    Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.45f)),
                )
            }
            StepPath(session.steps)
        }
    }
}

@Composable
private fun DeviceChip(session: WebSessionDto) {
    val parts = listOfNotNull(session.device?.lowercase()?.replaceFirstChar { it.uppercase() }, session.browser)
    if (parts.isEmpty()) return
    Box(Modifier.attrsModifier { classes("qp-step") }) { SpanText(parts.joinToString(" · ")) }
}

@Composable
private fun SourceChip(session: WebSessionDto) {
    val label = session.utmSource ?: session.referrer ?: PlatformT.sessionDirect
    Box(Modifier.attrsModifier { classes("qp-step") }) { SpanText(label) }
}

@Composable
private fun StepPath(steps: List<WebSessionStepDto>) {
    if (steps.isEmpty()) return
    // Collapse the middle when very long so the row stays scannable.
    val shown =
        if (steps.size <= 12) steps
        else steps.take(6) + WebSessionStepDto("MORE", "+${steps.size - 10}", "") + steps.takeLast(4)
    Row(
        Modifier.fillMaxWidth().styleModifier { property("overflow-x", "auto"); property("flex-wrap", "nowrap") }
            .gap(4.px).attrsModifier { classes("qk-hide-scrollbar") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        shown.forEachIndexed { i, step ->
            if (i > 0) SpanText("→", Modifier.attrsModifier { classes("qp-step-arrow") })
            val isCta = step.type == "CLICK" && (step.label.startsWith("cta-") || step.label.startsWith("plan-cta-"))
            Box(
                Modifier.whiteSpace(WhiteSpace.NoWrap)
                    .attrsModifier { classes(*listOfNotNull("qp-step", "cta".takeIf { isCta }).toTypedArray()) },
            ) {
                SpanText(stepLabel(step))
            }
        }
    }
}

private fun stepLabel(step: WebSessionStepDto): String =
    when (step.type) {
        "PAGEVIEW" -> step.label
        "SECTION_VIEW" -> "#${step.label}"
        "CLICK" -> "⊙ ${step.label}"
        "LANG_SWITCH" -> step.label.replace(">", "→")
        "MORE" -> step.label
        else -> step.label
    }

@Composable
private fun Badge(
    text: String,
    color: String,
) {
    Box(
        Modifier.padding(topBottom = 3.px, leftRight = 8.px).borderRadius(999.px)
            .styleModifier { property("background", "${color}22"); property("border", "1px solid ${color}66") },
    ) {
        SpanText(text, Modifier.fontSize(10.px).fontWeight(FontWeight.Bold).styleModifier { property("color", color) })
    }
}

@Composable
private fun LiveBadge() {
    Row(
        Modifier.padding(topBottom = 3.px, leftRight = 8.px).borderRadius(999.px).gap(5.px)
            .styleModifier { property("background", "rgba(52,211,153,.14)"); property("border", "1px solid rgba(52,211,153,.4)") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(Modifier.size(7.px).borderRadius(999.px).backgroundColor(Color.rgb(0x34, 0xD3, 0x99)).attrsModifier { classes("qp-pulse") })
        SpanText(PlatformT.sessionLive, Modifier.fontSize(10.px).fontWeight(FontWeight.Bold).color(Color.rgb(0x7E, 0xE2, 0xBC)))
    }
}

private fun geoLabel(session: WebSessionDto): String =
    when {
        session.city != null -> session.city!!
        session.country != null -> countryName(session.country!!)
        else -> "—"
    }

/** ISO instant -> short relative time using the platform strings. */
private fun relativeTime(iso: String): String {
    val t = Date.parse(iso)
    if (t.isNaN()) return ""
    val diffSec = ((Date.now() - t) / 1000.0).toLong().coerceAtLeast(0)
    return when {
        diffSec < 60 -> PlatformT.relJustNow
        diffSec < 3600 -> "${diffSec / 60} ${PlatformT.relMinAgo}"
        diffSec < 86400 -> "${diffSec / 3600} ${PlatformT.relHourAgo}"
        else -> "${diffSec / 86400} ${PlatformT.relDayAgo}"
    }
}
